export type PaymentPlan = {
  id: number;
  planCode: string;
  planType?: string;
  planCategory?: string;
  planName: string;
  level: number;
  description?: string;
  displaySubtitle?: string;
  displayBadge?: string;
  displayFeatures?: string[];
  billingCycle: string;
  durationDays: number;
  price: number | string;
  currency?: string;
  creditQuota: number | string;
  rolloverPolicy?: string;
  rolloverRatio?: number | string | null;
  rateLimitRpm?: number;
  current?: boolean;
  purchasable?: boolean;
  actionLabel?: string;
  actionDisabled?: boolean;
  purchaseLimit?: number;
  enabled?: number | boolean;
  sortOrder?: number;
};

export type PaymentPlanTab = {
  tabKey: string;
  tabName: string;
  planCategory: string;
  plans: PaymentPlan[];
};

export type CurrentSubscriptionSummary = {
  hasSubscription?: boolean;
  planCode?: string;
  planName?: string;
  currentPeriodEnd?: string;
  daysRemaining?: number;
  paidSubscriptionActive?: boolean;
};

export type PaymentPlanList = {
  tabs: PaymentPlanTab[];
  defaultTabKey?: string;
  paidSubscriptionActive?: boolean;
  subscriptionPurchasable?: boolean;
  rechargeEligible?: boolean;
  currentSubscription?: CurrentSubscriptionSummary;
};

export const normalizePlans = (plans: any): PaymentPlan[] => {
  if (!Array.isArray(plans)) {
    return [];
  }

  return plans
    .filter((item) => item && typeof item === 'object')
    .map((item) => ({ ...item }))
    .sort((a, b) => Number(a?.level || 0) - Number(b?.level || 0));
};

export const extractPlanTabs = (plansRaw: any): PaymentPlanTab[] => {
  return normalizePlanList(plansRaw).tabs;
};

export const normalizePlanList = (plansRaw: any): PaymentPlanList => {
  if (!plansRaw) {
    return { tabs: [] };
  }

  const normalizeTab = (tab: any): PaymentPlanTab | null => {
    if (!tab || typeof tab !== 'object') {
      return null;
    }
    const tabKey = String(tab.tabKey || tab.planCategory || '');
    const plans = normalizePlans(tab.plans);
    if (!tabKey || plans.length === 0) {
      return null;
    }
    return {
      tabKey,
      tabName: String(tab.tabName || ''),
      planCategory: String(tab.planCategory || tabKey),
      plans,
    };
  };

  const buildFromPayload = (payload: any): PaymentPlanTab[] => {
    if (!payload || typeof payload !== 'object') {
      return [];
    }
    if (Array.isArray(payload.tabs)) {
      return payload.tabs.map(normalizeTab).filter(Boolean) as PaymentPlanTab[];
    }
    if (Array.isArray(payload)) {
      return [
        {
          tabKey: 'DEFAULT',
          tabName: '套餐',
          planCategory: 'DEFAULT',
          plans: normalizePlans(payload),
        },
      ].filter((tab) => tab.plans.length > 0);
    }
    return [];
  };

  const rawData = plansRaw && typeof plansRaw === 'object' && 'data' in plansRaw ? plansRaw.data : plansRaw;
  const direct = buildFromPayload(rawData);
  if (direct.length > 0) {
    return {
      tabs: direct,
      defaultTabKey: rawData?.defaultTabKey,
      paidSubscriptionActive: rawData?.paidSubscriptionActive,
      subscriptionPurchasable: rawData?.subscriptionPurchasable,
      rechargeEligible: rawData?.rechargeEligible,
      currentSubscription: rawData?.currentSubscription,
    };
  }

  if (typeof rawData === 'string') {
    try {
      const parsed = JSON.parse(rawData);
      const inner = parsed && typeof parsed === 'object' && 'data' in parsed ? parsed.data : parsed;
      return {
        tabs: buildFromPayload(inner),
        defaultTabKey: inner?.defaultTabKey,
        paidSubscriptionActive: inner?.paidSubscriptionActive,
        subscriptionPurchasable: inner?.subscriptionPurchasable,
        rechargeEligible: inner?.rechargeEligible,
        currentSubscription: inner?.currentSubscription,
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to parse plans JSON:', error);
    }
  }

  return { tabs: [] };
};

export const flattenPlanTabs = (tabs: PaymentPlanTab[]): PaymentPlan[] => tabs.flatMap((tab) => tab.plans);

export const normalizeCycleKey = (c?: string) => {
  const s = String(c || '').toUpperCase();
  if (s === 'ANNUAL' || s === 'ANNUALLY') return 'YEARLY';
  if (s === 'BIWEEKLY' || s === 'TWO_WEEKS') return 'TWO_WEEK';
  return s;
};
